/**
 * MediaProvider — fetches admin-overridable image slots + gallery lists.
 * Falls back to defaults from /lib/config.js when a slot is empty.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "./config";

const MediaCtx = createContext(null);

/** Convert relative /api/media/xxx → full URL through REACT_APP_BACKEND_URL. */
export const resolveMediaUrl = (u) => {
  if (!u) return u;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return `${API_BASE}${u}`;
  return u;
};

export function MediaProvider({ children }) {
  const [slots, setSlots] = useState({});
  const [galleries, setGalleries] = useState({}); // division_id → [{id,url}]
  const [loading, setLoading] = useState(true);

  const refreshSlots = useCallback(async () => {
    try {
      const r = await api.get("/media/slots");
      setSlots(r.data || {});
    } catch { /* keep fallbacks */ }
  }, []);

  const refreshGallery = useCallback(async (divisionId) => {
    try {
      const r = await api.get(`/media/gallery/${divisionId}`);
      setGalleries((prev) => ({ ...prev, [divisionId]: r.data || [] }));
      return r.data || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    refreshSlots().finally(() => setLoading(false));
  }, [refreshSlots]);

  /** Get an image URL for a slot; fallback to `defaultUrl` if not set. */
  const getSlot = useCallback(
    (key, defaultUrl) => resolveMediaUrl(slots[key]) || defaultUrl,
    [slots],
  );

  /** Get gallery list for a division. Triggers fetch on first access. */
  const getGallery = useCallback(
    (divisionId) => {
      if (!(divisionId in galleries)) {
        refreshGallery(divisionId);
        return null; // signal loading
      }
      return galleries[divisionId].map((g) => ({
        ...g,
        url: resolveMediaUrl(g.url),
      }));
    },
    [galleries, refreshGallery],
  );

  return (
    <MediaCtx.Provider value={{ slots, loading, getSlot, getGallery, refreshSlots, refreshGallery }}>
      {children}
    </MediaCtx.Provider>
  );
}

export const useMedia = () => useContext(MediaCtx) || {
  slots: {}, loading: true,
  getSlot: (_, d) => d, getGallery: () => null,
  refreshSlots: () => {}, refreshGallery: () => {},
};
